import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { queue } from "@/components/Toast";
import { pendingSignalCountKey } from "@/hooks/usePendingSignalCount";
import { POLL_CURATION_MS } from "@/lib/realtime";
import { useIsBrandLive, useLiveRefetchInterval } from "@/lib/socket";
import { useAuthStore } from "@/stores/authStore";
import type { MarketSignal } from "@/types/marketSignal.type";
import { brandUniverseGraphKey } from "../../api/universeCache";
import {
  bumpPendingCount,
  cancelSignalFetches,
  getSignal,
  marketSignalsKey,
  optimisticSignalId,
  patchSignal,
  putSignal,
  settleSignalsWrite,
  signalsWriteKey,
} from "./signalsCache";

/** What a failed write puts back: the one signal it touched, and the badge if it moved. */
type SignalWriteContext = { previous?: MarketSignal; bumped: boolean };

type CreateSignalContext = { tempId: string | null; bumped: boolean };

export interface CreateSignalVars {
  kind: string;
  title: string;
  body: string;
  confidence?: number | null;
  // When true the author is their own curator: created `active` with the chosen lifetime.
  activate?: boolean;
  validUntil?: string | null; // ISO date, or null for "until superseded"
}

export interface UpdateSignalVars {
  signalId: string;
  kind?: string;
  title?: string;
  body?: string;
  confidence?: number | null;
  validUntil?: string | null;
  recommendedValidUntil?: string | null;
}

/**
 * One brand's market signals (all statuses) + the lifecycle mutations.
 * The page groups/filters client-side, so a single list query backs everything.
 *
 * Every write is optimistic, carries `signalsWriteKey` and settles through
 * `settleSignalsWrite`, so a teammate's realtime hint never lands on top of it.
 */
export function useMarketSignals(brandId: string | null) {
  const qc = useQueryClient();
  const live = useIsBrandLive(brandId);
  const refetchInterval = useLiveRefetchInterval(brandId, POLL_CURATION_MS);
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: marketSignalsKey(brandId) });
    // keep the sidebar pending badge in sync
    void qc.invalidateQueries({ queryKey: pendingSignalCountKey(brandId) });
  };

  const query = useQuery({
    queryKey: marketSignalsKey(brandId),
    // Signals are filed by field reporter runs, the chat agents' SetMarketSignal and
    // teammates: pushed as realtime hints while the brand is live, polled otherwise.
    refetchInterval,
    refetchOnWindowFocus: !live,
    queryFn: async () => {
      const res = await axiosInstance.get<{ signals: MarketSignal[] }>(
        `/market-signals/${brandId}`,
      );
      return res.data.signals;
    },
    enabled: !!brandId,
  });

  /** Writes one signal's expected outcome; the badge drops when it leaves pending. */
  const beginWrite = async (
    signalId: string,
    patch: Partial<MarketSignal>,
  ): Promise<SignalWriteContext> => {
    await cancelSignalFetches(qc, brandId);
    const previous = getSignal(qc, brandId, signalId);
    patchSignal(qc, brandId, signalId, patch);
    const bumped =
      previous?.status === "pending" && patch.status !== undefined && patch.status !== "pending";
    if (bumped) bumpPendingCount(qc, brandId, -1);
    return { previous, bumped };
  };

  /** Puts back only the signal a failed write touched, never a whole-list snapshot. */
  const rollback = (context: unknown) => {
    const ctx = context as SignalWriteContext | undefined;
    if (ctx?.previous) putSignal(qc, brandId, ctx.previous);
    if (ctx?.bumped) bumpPendingCount(qc, brandId, 1);
  };

  const settle = (error: unknown) => settleSignalsWrite(qc, brandId, { refetch: !!error });

  const createSignal = useMutation({
    mutationKey: signalsWriteKey(brandId),
    mutationFn: async (vars: CreateSignalVars) => {
      const res = await axiosInstance.post<MarketSignal>(
        `/market-signals/${brandId}`,
        vars,
      );
      return res.data;
    },
    onMutate: async (vars): Promise<CreateSignalContext> => {
      await cancelSignalFetches(qc, brandId);
      if (!qc.getQueryData(marketSignalsKey(brandId))) return { tempId: null, bumped: false };
      const tempId = optimisticSignalId();
      const now = new Date().toISOString();
      const viewerId = useAuthStore.getState().user?.id ?? null;
      const status = vars.activate ? "active" : "pending";
      const temp: MarketSignal = {
        id: tempId,
        brandId: brandId ?? "",
        kind: vars.kind,
        title: vars.title,
        body: vars.body,
        confidence: vars.confidence ?? null,
        recommendedValidUntil: null,
        validFrom: now,
        validUntil: vars.activate ? (vars.validUntil ?? null) : null,
        status,
        groundingObservations: [],
        groundingFactIds: [],
        sourceRunId: null,
        supersededBySignalId: null,
        graduatedFactId: null,
        graduatedAt: null,
        acceptedByUserId: vars.activate ? viewerId : null,
        acceptedAt: vars.activate ? now : null,
        createdByUserId: viewerId,
        createdAt: now,
        updatedAt: now,
      };
      // Newest first, as the list is served.
      qc.setQueryData<MarketSignal[]>(marketSignalsKey(brandId), (signals) =>
        signals ? [temp, ...signals] : signals,
      );
      const bumped = status === "pending";
      if (bumped) bumpPendingCount(qc, brandId, 1);
      return { tempId, bumped };
    },
    onSuccess: (created, _vars, ctx) => {
      qc.setQueryData<MarketSignal[]>(marketSignalsKey(brandId), (signals) =>
        signals
          ?.filter((s) => s.id !== created.id)
          .map((s) => (s.id === ctx?.tempId ? created : s)),
      );
      queue.add({ title: "Signal created", variant: "success" });
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.tempId) {
        qc.setQueryData<MarketSignal[]>(marketSignalsKey(brandId), (signals) =>
          signals?.filter((s) => s.id !== ctx.tempId),
        );
      }
      if (ctx?.bumped) bumpPendingCount(qc, brandId, -1);
      queue.add({
        title: "Couldn't create the signal",
        variant: "error",
        action: { label: "Try again", onPress: () => createSignal.mutate(vars) },
      });
    },
    onSettled: (_data, error) => settle(error),
  });

  const acceptSignal = useMutation({
    mutationKey: signalsWriteKey(brandId),
    // `quiet`: the caller reports the outcome itself (the phone deck's own toast).
    mutationFn: async (vars: { signalId: string; validUntil: string | null; quiet?: boolean }) => {
      const res = await axiosInstance.post<MarketSignal>(
        `/market-signals/${brandId}/${vars.signalId}/accept`,
        { validUntil: vars.validUntil },
      );
      return res.data;
    },
    // The board keeps the card on screen until its exit animation ends; the
    // badge drops at once.
    onMutate: (vars) => {
      const now = new Date().toISOString();
      return beginWrite(vars.signalId, {
        status: "active",
        validFrom: now,
        validUntil: vars.validUntil,
        acceptedAt: now,
        acceptedByUserId: useAuthStore.getState().user?.id ?? null,
      });
    },
    onSuccess: (updated, vars) => {
      putSignal(qc, brandId, updated);
      if (!vars.quiet) queue.add({ title: "Signal accepted", variant: "success" });
    },
    onError: (_err, vars, context) => {
      rollback(context);
      if (!vars.quiet) {
        queue.add({
          title: "Couldn't accept the signal",
          description: "It is back at the top of the queue. Nothing changed.",
          variant: "error",
          caps: true,
          action: { label: "Try again", onPress: () => acceptSignal.mutate(vars) },
        });
      }
    },
    onSettled: (_data, error) => settle(error),
  });

  const updateSignal = useMutation({
    mutationKey: signalsWriteKey(brandId),
    mutationFn: async (vars: UpdateSignalVars) => {
      const { signalId, ...patch } = vars;
      const res = await axiosInstance.patch<MarketSignal>(
        `/market-signals/${brandId}/${signalId}`,
        patch,
      );
      return res.data;
    },
    onMutate: ({ signalId, ...fields }) => {
      const patch = Object.fromEntries(
        Object.entries(fields).filter(([, value]) => value !== undefined),
      ) as Partial<MarketSignal>;
      return beginWrite(signalId, patch);
    },
    onSuccess: (updated) => {
      putSignal(qc, brandId, updated);
      queue.add({ title: "Signal updated", variant: "success" });
    },
    onError: (_err, _vars, context) => {
      rollback(context);
      queue.add({ title: "Couldn't update the signal", variant: "error" });
    },
    onSettled: (_data, error) => settle(error),
  });

  // An id, or the id with `quiet` when the caller reports the outcome itself.
  const dismissSignal = useMutation({
    mutationKey: signalsWriteKey(brandId),
    mutationFn: async (vars: string | { signalId: string; quiet?: boolean }) => {
      const signalId = typeof vars === "string" ? vars : vars.signalId;
      const res = await axiosInstance.post<MarketSignal>(
        `/market-signals/${brandId}/${signalId}/dismiss`,
      );
      return res.data;
    },
    onMutate: (vars) =>
      beginWrite(typeof vars === "string" ? vars : vars.signalId, { status: "dismissed" }),
    onSuccess: (updated, vars) => {
      putSignal(qc, brandId, updated);
      if (typeof vars === "string" || !vars.quiet) queue.add({ title: "Signal dismissed", variant: "info" });
    },
    onError: (_err, vars, context) => {
      rollback(context);
      if (typeof vars === "string" || !vars.quiet) {
        queue.add({ title: "Couldn't dismiss the signal", variant: "error" });
      }
    },
    onSettled: (_data, error) => settle(error),
  });

  const graduateSignal = useMutation({
    mutationKey: signalsWriteKey(brandId),
    mutationFn: async (signalId: string) => {
      const res = await axiosInstance.post<MarketSignal>(
        `/market-signals/${brandId}/${signalId}/graduate`,
      );
      return res.data;
    },
    onMutate: (signalId) =>
      beginWrite(signalId, { status: "graduated", graduatedAt: new Date().toISOString() }),
    onSuccess: (updated) => {
      putSignal(qc, brandId, updated);
      // The new fact's hint skips this tab, so the universe refreshes here.
      void qc.invalidateQueries({ queryKey: brandUniverseGraphKey(brandId) });
      queue.add({ title: "Signal graduated to a permanent fact", variant: "success" });
    },
    onError: (_err, _vars, context) => {
      rollback(context);
      queue.add({ title: "Couldn't graduate the signal", variant: "error" });
    },
    onSettled: (_data, error) => settle(error),
  });

  return {
    signals: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    invalidate,
    createSignal,
    acceptSignal,
    updateSignal,
    dismissSignal,
    graduateSignal,
  };
}
